import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Input, Select, Modal, Form, Typography,
  Alert, Radio, Button, Flex, App as AntApp, DatePicker, Row, Col,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import type { Transacao } from './types';
import { getToken, fmt } from './utils';
import { ModalNovoFornecedor } from '../financeiro-contas-pagar/ModalNovoFornecedor';
import { opcoesFornecedor, filtrarFornecedor, FornecedorSlim } from '../../utils/fornecedores';
import { ModalNovoCentroCusto } from '../financeiro-contas-pagar/ModalNovoCentroCusto';
import { TIPO_RECEITA_OPTIONS } from '../financeiro-contas-receber/utils';

const { Text } = Typography;

// Modal "Criar lançamento novo (a partir desta transação)".
// CP (2026-07-20, pedido do Lucas): mesmo formulário do "+ Nova despesa" —
// fornecedor do cadastro (+ novo), categoria do PLANO (deriva o grupo DRE) e
// centro de custo (+ novo indireta). Cria a despesa DE VERDADE no Contas a
// Pagar (POST /contas-pagar, já paga na data da transação) e concilia em
// seguida (baixa unificada D1). Antes: categoria era texto livre e a despesa
// ia pra lancamentos_manuais.
// CR: mantém o fluxo antigo (parcela recebida em contas_a_receber).
export default function ModalCriarLancamento({ transacao, onFechar, onCriou }: {
  transacao: Transacao; onFechar: () => void; onCriou: () => void;
}) {
  const { message } = AntApp.useApp();
  const ehReceita = transacao.valor > 0;
  const tipoDefault: 'CP' | 'CR' = ehReceita ? 'CR' : 'CP';
  const dataTrans = String(transacao.data_transacao).slice(0, 10);
  const [tipo, setTipo] = useState<'CP' | 'CR'>(tipoDefault);
  const [descricao, setDescricao] = useState(transacao.descricao.split(/[:·\-,]/)[0].trim());
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Datas editáveis (pedido do Lucas 2026-07-21): competência e vencimento
  // nascem na data da transação mas podem ser ajustadas; o PAGAMENTO fica
  // preso na data da transação (é a âncora da conciliação).
  // CR — classificação da receita. Vazio = o servidor decide (ele reconhece
  // rendimento de aplicação pela descrição do extrato). 🚨 A régua NÃO é
  // copiada para cá de propósito: ela já vive em dois lambdas, e uma terceira
  // cópia na tela seria a que ninguém lembraria de atualizar.
  const [tipoReceita, setTipoReceita] = useState<string | undefined>();
  const [competencia, setCompetencia] = useState(dataTrans);
  const [vencimento, setVencimento] = useState(dataTrans);
  const [metodoPagamento, setMetodoPagamento] = useState<string | undefined>(() => {
    const d = transacao.descricao.toLowerCase();
    if (d.includes('pix')) return 'PIX';
    if (d.includes('ted') || d.includes('transfer')) return 'TED';
    if (d.includes('boleto') || d.includes('pagamento efetuado')) return 'Boleto';
    if (d.includes('cart')) return 'Cartão';
    return undefined;
  });

  // CP — mesmos metadados do modal Nova Despesa
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const [categorias, setCategorias] = useState<any[]>([]);
  const [centrosCusto, setCentrosCusto] = useState<any[]>([]);
  const [fornecedorId, setFornecedorId] = useState<string | undefined>();
  const fornecedorSelecionado: FornecedorSlim | null =
    fornecedores.find((f: any) => f.id === fornecedorId) || null;
  // Sugestão vinda da IA (Haiku) quando o match determinístico não achou nada.
  const [sugestaoIa, setSugestaoIa] = useState<{ nome: string; confianca: string } | null>(null);
  const [categoriaDre, setCategoriaDre] = useState<string | undefined>();
  const [grupoDre, setGrupoDre] = useState('');
  const [centroCusto, setCentroCusto] = useState<string | undefined>();
  const [novoFornAberto, setNovoFornAberto] = useState(false);
  const [fornEditando, setFornEditando] = useState<FornecedorSlim | null>(null);
  const [novoCcAberto, setNovoCcAberto] = useState(false);

  const un = (r: any) => (r.data?.body ? JSON.parse(r.data.body) : r.data);

  // Normaliza pra casar nomes com a descrição do banco (sem acento/caixa)
  const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const [rF, rC, rCC] = await Promise.all([
          axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/centros-custo?status=todos&simples=1`, { headers: { Authorization: token } }).catch(() => ({ data: [] })),
        ]);
        const dF = un(rF); const listaF = Array.isArray(dF) ? dF : [];
        setFornecedores(listaF);
        const dC = un(rC); const listaC = Array.isArray(dC) ? dC.filter((c: any) => c.linha_dre) : [];
        setCategorias(listaC);
        const dCC = un(rCC); setCentrosCusto(Array.isArray(dCC) ? dCC : []);

        // Pré-preenchimento a partir da transação (pedido do Lucas 2026-07-21;
        // reforçado 2026-07-27): casa primeiro contra o DESTINATÁRIO puro do
        // OFX (name_original, sem o ruído do memo), depois contra a descrição.
        // Match mais longo vence; considera nome fantasia E razão social.
        const alvoTexto = (transacao.name_original || '').trim() || transacao.descricao;
        const alvos = [norm(alvoTexto), norm(transacao.descricao)];
        let melhor: any = null; let melhorLen = 0;
        for (const f of listaF) {
          for (const nome of [f.nome_fantasia, f.razao_social]) {
            const nomeN = norm(nome || '');
            if (nomeN.length >= 4 && nomeN.length > melhorLen && alvos.some((a) => a.includes(nomeN))) {
              melhor = f; melhorLen = nomeN.length;
            }
          }
        }
        if (melhor) setFornecedorId(melhor.id);
        // Sem match local e é despesa → pede sugestão ao backend (determinístico
        // com razão social + fallback Haiku). Best-effort, não trava o modal.
        else if (transacao.valor < 0 && listaF.length) {
          (async () => {
            try {
              const r = await axios.post(`${API_URL}/financeiro/fornecedores/sugerir`,
                { texto: alvoTexto }, { headers: { Authorization: token } });
              const d = un(r);
              if (d?.fornecedor?.id) {
                setFornecedorId((atual) => atual || d.fornecedor.id);
                if (d.origem === 'ia') setSugestaoIa({ nome: d.fornecedor.nome_fantasia, confianca: d.confianca || '' });
              }
            } catch { /* sugestão é opcional */ }
          })();
        }
        const catTrans = norm((transacao as any).categoria || '');
        if (catTrans) {
          const cat = listaC.find((c: any) => norm(c.linha_dre) === catTrans);
          if (cat) { setCategoriaDre(cat.linha_dre); setGrupoDre(cat.grupo_dre || ''); }
        }
      } catch { /* selects ficam vazios; o CR segue funcionando */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function recarregarFornecedores(novoId?: string) {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000`, { headers: { Authorization: token } });
    const d = un(r);
    setFornecedores(Array.isArray(d) ? d : []);
    if (novoId) setFornecedorId(novoId);
  }

  async function recarregarCentrosCusto(novoCodigo?: string) {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/centros-custo?status=todos&simples=1`, { headers: { Authorization: token } });
    const d = un(r);
    setCentrosCusto(Array.isArray(d) ? d : []);
    if (novoCodigo) setCentroCusto(novoCodigo);
  }

  async function criar() {
    setSalvando(true);
    try {
      const token = await getToken();
      const obsFinal = (observacoes ? observacoes + '\n' : '')
        + `Criado via conciliação OFX (FITID ${(transacao as any).fitid || '—'})`;

      if (tipo === 'CP') {
        if (!categoriaDre) { message.warning('Escolha a categoria'); setSalvando(false); return; }
        // 1. cria a despesa REAL no Contas a Pagar, já paga na data da transação
        const forn = fornecedores.find((f: any) => f.id === fornecedorId);
        const cc = centrosCusto.find((x: any) => x.codigo_canonical === centroCusto);
        const rCria = await axios.post(`${API_URL}/contas-pagar`, {
          fornecedor_id: fornecedorId || null,
          fornecedor_original: forn?.nome_fantasia || null,
          data_competencia: competencia || dataTrans,
          data_vencimento: vencimento || dataTrans,
          data_pagamento: dataTrans,
          metodo_pagamento: metodoPagamento || null,
          descricao: descricao.trim() || transacao.descricao,
          valor: Math.abs(transacao.valor),
          categoria_dre: categoriaDre,
          grupo_dre: grupoDre || null,
          centros_custo: cc ? [{
            centro: cc.codigo_canonical,
            valor: Math.abs(transacao.valor),
            cc_contrato_numero: cc.codigo_canonical,
            cc_contrato_titulo: cc.nome_completo || null,
          }] : [],
          modo: 'avista',
          parcelas: 1,
          status: 'pago',
          conta_financeira: (transacao as any).conta_label || null,
          observacoes: obsFinal,
        }, { headers: { Authorization: token } });
        const dCria = un(rCria);
        const novoId = Array.isArray(dCria?.ids) ? dCria.ids[0] : null;
        if (!novoId) throw new Error('Despesa criada mas o id não voltou');
        // 2. concilia (vincula + baixa unificada D1)
        await axios.post(`${API_URL}/financeiro/transacoes-bancarias/${transacao.id}/conciliar`,
          { tipo: 'lancamento_pagar', alvo_id: novoId },
          { headers: { Authorization: token } });
        message.success('Despesa criada no Contas a Pagar e conciliada.');
      } else {
        // CR — fluxo original (parcela recebida)
        const r = await axios.post(`${API_URL}/financeiro/transacoes-bancarias/${transacao.id}/criar-lancamento`,
          { tipo: 'CR', categoria_dre: 'Receita', descricao, observacoes,
            // undefined = deixa o servidor classificar pela descrição.
            tipo_receita: tipoReceita },
          { headers: { Authorization: token } });
        // A mensagem do servidor diz o que ele classificou — 8 s porque é
        // informação, não confirmação.
        message.success(un(r).mensagem, 8);
      }
      onCriou();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.response?.data?.erro || e.message);
    } finally { setSalvando(false); }
  }

  return (
    <Modal
      open onCancel={onFechar} onOk={criar} confirmLoading={salvando}
      okText="✓ Criar + Vincular" cancelText="Cancelar" width={620}
      title="📝 Criar lançamento novo (a partir desta transação)"
      okButtonProps={{
        disabled: tipo === 'CP' && !categoriaDre,
        style: { background: '#0a7f4a', borderColor: '#0a7f4a' },
      }}
    >
      <Card size="small" style={{ background: '#FAFBFF', marginBottom: 14 }} bodyStyle={{ padding: 10 }}>
        <Text strong>{fmtDataBr(transacao.data_transacao)}</Text> · {transacao.descricao}
        <div style={{ marginTop: 4 }}>
          <Text style={{ color: ehReceita ? '#0a7f4a' : '#B91626', fontWeight: 700, fontSize: 16 }}>
            {ehReceita ? '+' : ''}{fmt(transacao.valor)} {ehReceita ? '(entrada)' : '(saída)'}
          </Text>
        </div>
      </Card>

      <Form layout="vertical" size="small">
        <Form.Item label="Tipo de lançamento"
          extra={tipo === 'CP'
            ? 'Cria a despesa no Contas a Pagar (já paga na data da transação) e concilia.'
            : 'Cria parcela em CR, marca como recebida.'}>
          <Radio.Group value={tipo} onChange={(e) => setTipo(e.target.value)} optionType="button" buttonStyle="solid" style={{ width: '100%' }}>
            <Radio.Button value="CP" style={{ width: '50%', textAlign: 'center' }}>📤 Contas a Pagar (despesa)</Radio.Button>
            <Radio.Button value="CR" style={{ width: '50%', textAlign: 'center' }}>📥 Contas a Receber (receita)</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {tipo === 'CP' && (
          <>
            <Form.Item label="Fornecedor"
              extra={sugestaoIa && fornecedorId
                ? <Text style={{ fontSize: 11, color: '#b06a00' }}>🤖 Sugerido por IA ({sugestaoIa.nome}) — confira antes de salvar.</Text>
                : undefined}>
              <Flex gap={4}>
                <Select
                  value={fornecedorId}
                  onChange={(v) => { setFornecedorId(v); setSugestaoIa(null); }}
                  placeholder="Buscar fornecedor"
                  allowClear showSearch filterOption={filtrarFornecedor}
                  options={opcoesFornecedor(fornecedores, { avisarSemCnpj: true })}
                  style={{ flex: 1, minWidth: 0 }} />
                <Button size="small" icon={<PlusOutlined />} style={{ flexShrink: 0 }} onClick={() => setNovoFornAberto(true)}
                  title="Cadastrar novo fornecedor" />
                <Button size="small" icon={<EditOutlined />} style={{ flexShrink: 0 }}
                  disabled={!fornecedorSelecionado}
                  onClick={() => setFornEditando(fornecedorSelecionado)}
                  title={fornecedorSelecionado
                    ? `Editar ${fornecedorSelecionado.nome_fantasia} (CNPJ, razão social…)`
                    : 'Escolha um fornecedor para editar'} />
              </Flex>
            </Form.Item>

            <Form.Item label="Categoria" required
              extra={grupoDre ? `Entra no DRE em: ${grupoDre}` : 'A categoria define o grupo do DRE.'}>
              <Select
                value={categoriaDre}
                onChange={(v) => {
                  const c = categorias.find((c: any) => c.linha_dre === v);
                  setCategoriaDre(v || undefined);
                  setGrupoDre(c?.grupo_dre || '');
                }}
                placeholder="Selecione a categoria do plano"
                allowClear showSearch optionFilterProp="label"
                options={categorias.map((c: any) => ({
                  label: `${c.linha_dre} (${c.grupo_dre})`,
                  value: c.linha_dre,
                }))} />
            </Form.Item>

            <Form.Item label="Centro de Custo"
              extra="Contratos criam o próprio CC automaticamente; o + cadastra um centro de indiretas.">
              <Flex gap={4}>
                <Select
                  value={centroCusto}
                  onChange={(v) => setCentroCusto(v)}
                  placeholder="Selecione o centro de custo (opcional)"
                  allowClear showSearch optionFilterProp="label"
                  options={[...centrosCusto]
                    .sort((a: any, b: any) => ((a.fechado ? 2 : a.tipo === 'estrutural' ? 1 : 0)
                      - (b.fechado ? 2 : b.tipo === 'estrutural' ? 1 : 0)))
                    .map((cc: any) => ({
                      label: `${cc.codigo_canonical}${cc.nome_completo ? ` — ${cc.nome_completo}` : ''}`
                        + (cc.tipo === 'estrutural' ? ' · indireta' : '') + (cc.fechado ? ' · fechado' : ''),
                      value: cc.codigo_canonical,
                    }))}
                  style={{ flex: 1, minWidth: 0 }} />
                <Button size="small" icon={<PlusOutlined />} style={{ flexShrink: 0 }} onClick={() => setNovoCcAberto(true)}
                  title="Cadastrar novo centro de custo (indireta)" />
              </Flex>
            </Form.Item>

            <Row gutter={8}>
              <Col span={8}>
                <Form.Item label="Competência" extra="Mês contábil (DRE)">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                    value={competencia ? dayjs(competencia) : null}
                    onChange={(d) => setCompetencia(d ? d.format('YYYY-MM-DD') : dataTrans)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Vencimento">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                    value={vencimento ? dayjs(vencimento) : null}
                    onChange={(d) => setVencimento(d ? d.format('YYYY-MM-DD') : dataTrans)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Forma de pagamento">
                  <Select value={metodoPagamento} onChange={(v) => setMetodoPagamento(v || undefined)}
                    allowClear placeholder="—"
                    options={[
                      { label: 'PIX', value: 'PIX' }, { label: 'Boleto', value: 'Boleto' },
                      { label: 'TED', value: 'TED' }, { label: 'Cartão', value: 'Cartão' },
                      { label: 'Dinheiro', value: 'Dinheiro' }, { label: 'Cheque', value: 'Cheque' },
                    ]} />
                </Form.Item>
              </Col>
            </Row>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message={<Text style={{ fontSize: 11 }}>
                Pagamento fixo em <strong>{fmtDataBr(dataTrans)}</strong>
                {(transacao as any).conta_label ? <> · conta <strong>{(transacao as any).conta_label}</strong></> : null}
                {' '}(vem da transação bancária — é a âncora da conciliação).
              </Text>} />
          </>
        )}
        {tipo === 'CR' && (
          <>
            <Form.Item
              label="Tipo de receita"
              extra={tipoReceita
                ? 'Escolhido à mão — o sistema não vai mexer.'
                : 'Deixe em automático: o sistema reconhece rendimento de aplicação pela descrição do extrato e já preenche competência, conta e a parte fiscal.'}
            >
              <Select
                value={tipoReceita}
                onChange={(v) => setTipoReceita(v || undefined)}
                allowClear
                placeholder="🤖 Automático (o sistema classifica)"
                options={TIPO_RECEITA_OPTIONS}
              />
            </Form.Item>
            <Alert
              type="info" showIcon style={{ marginBottom: 12 }}
              message={<Text style={{ fontSize: 11 }}>
                A parcela nasce <strong>recebida</strong> em {fmtDataBr(dataTrans)}, com a
                competência do extrato e a conta <strong>{(transacao as any).conta_label || '—'}</strong>.
                Receita de <strong>contrato</strong> entra na Receita Bruta da DRE e calcula
                retenção; <strong>rendimento</strong> e movimento patrimonial, não.
              </Text>}
            />
          </>
        )}

        <Form.Item label="Descrição">
          <Input value={descricao} onChange={e => setDescricao(e.target.value)} />
        </Form.Item>
        <Form.Item label="Observações">
          <Input.TextArea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2} />
        </Form.Item>
      </Form>

      {novoFornAberto && (
        <ModalNovoFornecedor
          onFechar={() => setNovoFornAberto(false)}
          onCriou={(id) => { setNovoFornAberto(false); recarregarFornecedores(id); }} />
      )}
      {fornEditando && (
        <ModalNovoFornecedor
          fornecedor={fornEditando}
          onFechar={() => setFornEditando(null)}
          onCriou={(id) => { setFornEditando(null); recarregarFornecedores(id || fornecedorId || undefined); }} />
      )}
      {novoCcAberto && (
        <ModalNovoCentroCusto
          onFechar={() => setNovoCcAberto(false)}
          onCriou={(codigo) => { setNovoCcAberto(false); recarregarCentrosCusto(codigo); }} />
      )}
    </Modal>
  );
}
